// One-time welcome modal for management (owner/manager/teamlead).
// Cannot be dismissed. User must confirm/update display name → success checkmark → auto-close.
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Check, User as UserIcon } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";

const MANAGER_ROLES = ["owner", "manager", "teamlead"];

export default function ManagerWelcomeModal() {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const inputRef = useRef(null);

  const shouldShow =
    user &&
    user._id &&
    MANAGER_ROLES.includes(user.role) &&
    !user.welcome_completed_at;

  useEffect(() => {
    if (shouldShow) setName(user.name || "");
  }, [shouldShow, user?.name]);

  useEffect(() => {
    if (shouldShow) setTimeout(() => inputRef.current?.focus(), 400);
  }, [shouldShow]);

  // Prevent ESC / backdrop from dismissing
  useEffect(() => {
    if (!shouldShow) return;
    const onKey = (e) => { if (e.key === "Escape") e.stopPropagation(); };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [shouldShow]);

  const canSubmit = name.trim().length >= 2 && !saving;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post("/users/me/complete-welcome", { name: name.trim() });
      // Show checkmark state FIRST — hold for ~1.6s before refreshing user,
      // otherwise refresh sets welcome_completed_at and unmounts before the animation plays.
      setSaved(true);
      setTimeout(async () => { await refreshUser(); }, 1600);
    } catch {
      setSaving(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="mw-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10001] grid place-items-center p-4"
        style={{
          background: "rgba(15, 20, 35, 0.55)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
        data-testid="manager-welcome-modal"
        // no onClick — modal is not dismissable
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 16 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="w-full max-w-md relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Card */}
          <div className="rounded-[28px] bg-white shadow-[0_40px_80px_-30px_rgba(15,20,35,0.35)] overflow-hidden relative">
            {/* Decorative gradient blob */}
            <div
              className="absolute -top-32 -right-24 w-80 h-80 rounded-full opacity-40 pointer-events-none"
              style={{ background: "radial-gradient(circle, hsl(var(--primary-soft)) 0%, transparent 65%)" }}
            />
            <div
              className="absolute -bottom-32 -left-24 w-80 h-80 rounded-full opacity-30 pointer-events-none"
              style={{ background: "radial-gradient(circle, hsl(45 92% 88%) 0%, transparent 65%)" }}
            />

            <AnimatePresence mode="wait">
              {!saved ? (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.25 }}
                  className="p-8 md:p-10 relative"
                >
                  {/* Waving icon */}
                  <motion.div
                    initial={{ y: -8, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                    className="mb-6 flex justify-center"
                  >
                    <motion.div
                      animate={{ rotate: [0, -14, 14, -10, 10, 0] }}
                      transition={{ duration: 1.6, delay: 0.5, repeat: Infinity, repeatDelay: 2.4 }}
                      className="w-16 h-16 rounded-[22px] bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(280_70%_55%)] grid place-items-center text-white shadow-[0_16px_36px_-14px_hsl(var(--primary)_/_0.6)]"
                      style={{ transformOrigin: "70% 80%" }}
                    >
                      <Sparkles size={26} strokeWidth={1.9} />
                    </motion.div>
                  </motion.div>

                  <motion.h2
                    initial={{ y: 8, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.15, duration: 0.4 }}
                    className="font-display text-[28px] md:text-[32px] font-bold text-center text-[hsl(var(--text-strong))] tracking-tight leading-tight"
                  >
                    {t("mw_title")}
                  </motion.h2>
                  <motion.p
                    initial={{ y: 8, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                    className="text-[14.5px] text-[hsl(var(--text-muted))] text-center mt-3 leading-relaxed max-w-sm mx-auto"
                  >
                    {t("mw_subtitle")}
                  </motion.p>

                  {/* Name input */}
                  <motion.form
                    initial={{ y: 12, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.28, duration: 0.4 }}
                    onSubmit={submit}
                    className="mt-8"
                  >
                    <label className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[hsl(var(--text-soft))] px-1">
                      {t("mw_your_name")}
                    </label>
                    <div className="mt-2 relative">
                      <UserIcon
                        size={16}
                        strokeWidth={2}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]"
                      />
                      <input
                        ref={inputRef}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("mw_placeholder")}
                        className="w-full h-14 rounded-2xl border hairline bg-white pl-11 pr-4 text-[16px] font-medium text-[hsl(var(--text-strong))] outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)_/_0.15)] transition"
                        data-testid="mw-name-input"
                        autoComplete="off"
                        maxLength={60}
                      />
                    </div>
                    <div className="mt-2 text-[12px] text-[hsl(var(--text-muted))] text-center leading-relaxed">
                      {t("mw_editable_hint")}
                    </div>

                    <button
                      type="submit"
                      disabled={!canSubmit}
                      data-testid="mw-continue"
                      className={`mt-6 w-full h-14 rounded-2xl font-semibold text-[15px] tracking-tight transition-all
                        ${canSubmit
                          ? "bg-[hsl(var(--text-strong))] text-white hover:opacity-90 active:scale-[0.99] shadow-[0_10px_24px_-10px_hsl(var(--text-strong)_/_0.5)]"
                          : "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] cursor-not-allowed"}`}
                    >
                      {saving ? t("mw_saving") : t("mw_cta")}
                    </button>
                  </motion.form>
                </motion.div>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.25 }}
                  className="p-10 md:p-14 flex flex-col items-center justify-center relative min-h-[400px]"
                >
                  <motion.div
                    initial={{ scale: 0.4, rotate: -20 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", stiffness: 300, damping: 16, delay: 0.05 }}
                    className="w-20 h-20 rounded-full bg-gradient-to-br from-[hsl(140_70%_50%)] to-[hsl(160_70%_45%)] grid place-items-center text-white shadow-[0_20px_44px_-14px_hsl(140_70%_45%_/_0.6)]"
                  >
                    <motion.div
                      initial={{ pathLength: 0, scale: 0.6 }}
                      animate={{ pathLength: 1, scale: 1 }}
                      transition={{ delay: 0.3, duration: 0.35, ease: "easeOut" }}
                    >
                      <Check size={38} strokeWidth={3} />
                    </motion.div>
                  </motion.div>
                  <motion.div
                    initial={{ y: 8, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.35 }}
                    className="font-display text-[22px] md:text-[24px] font-bold text-[hsl(var(--text-strong))] mt-6 tracking-tight text-center"
                  >
                    {t("mw_success_title")}
                  </motion.div>
                  <motion.div
                    initial={{ y: 8, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.35 }}
                    className="text-[14px] text-[hsl(var(--text-muted))] mt-2 text-center max-w-sm"
                  >
                    {t("mw_success_body").replace("{name}", name.trim())}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
